// @ts-nocheck
/* eslint-disable no-loop-func */
// Estudo 2.0 — UI primitives + helpers + config de tipos. Portado do protótipo Claude Design.
import React, { useEffect } from 'react';
import {
  IcoFlame, IcoTeach, IcoQuote, IcoQuestion, IcoQuiz, IcoInbox, IcoCrown, IcoTrophy,
  IcoTouch, IcoBulb, IcoBookmark, IcoPage,
} from './icons2';
import { splitMentions, MentionChip } from '../../mentions';

/* ---------- Avatar ---------- */
// Hash a letra/string num hue estável (autores reais têm nomes variados).
function hueOf(seed: string): number {
  const s = String(seed || '?');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}
export function Avatar({ user, size = 38, ring }: any) {
  const letter = (user?.avatar || user?.name?.[0] || '?').toString().toUpperCase().slice(0, 1);
  const hue = hueOf(user?.name || letter);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'grid', placeItems: 'center',
      background: `linear-gradient(150deg, hsl(${hue} 38% 30%), hsl(${hue} 30% 18%))`,
      color: 'var(--ink)', fontWeight: 600, fontSize: size * 0.4,
      border: ring ? `2px solid ${ring}` : '1px solid var(--line-strong)',
      fontFamily: 'var(--font-sans)', userSelect: 'none',
    }}>{letter}</div>
  );
}

/* ---------- Capa de livro (authorBook mini-pill) ---------- */
export function BookCover({ book, w = 14, h = 20, radius = 3 }: any) {
  const hue = book?.coverHue ?? 28;
  if (book?.coverUrl) {
    return <div style={{ width: w, height: h, borderRadius: radius, flexShrink: 0, background: `url(${book.coverUrl}) center/cover`, boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.25)' }} />;
  }
  return (
    <div style={{
      width: w, height: h, borderRadius: radius, flexShrink: 0, position: 'relative',
      background: `linear-gradient(135deg, hsl(${hue} 45% 42%), hsl(${hue} 50% 26%))`,
      boxShadow: 'inset -1px 0 0 rgba(0,0,0,0.25), inset 1px 0 0 rgba(255,255,255,0.08)',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', left: w * 0.18, top: 0, bottom: 0, width: 1, background: 'rgba(0,0,0,0.25)' }} />
    </div>
  );
}

/* ---------- Light-markdown renderer ---------- */
export function inlineRich(text: string, kb: string) {
  const out: any[] = [];
  // FP4 — fatia por menções ANTES do markdown; o resto segue o split de **/* normal.
  splitMentions(text || '').forEach((seg: any, si: number) => {
    if (seg.kind === 'mention') { out.push(<MentionChip key={kb + 'm' + si} type={seg.type} id={seg.id} handle={seg.handle} label={seg.label} />); return; }
    seg.value.split(/(\*\*[^*]+\*\*)/g).forEach((p: string, i: number) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) { out.push(<strong key={kb + 'b' + si + i} style={{ fontWeight: 600, color: 'var(--ink)' }}>{p.slice(2, -2)}</strong>); return; }
      p.split(/(\*[^*]+\*)/g).forEach((q: string, j: number) => {
        if (/^\*[^*]+\*$/.test(q)) out.push(<em key={kb + 'i' + si + i + j}>{q.slice(1, -1)}</em>);
        else if (q) out.push(<React.Fragment key={kb + 't' + si + i + j}>{q}</React.Fragment>);
      });
    });
  });
  return out;
}

export function RichText({ text, style }: any) {
  const lines = (text || '').split('\n');
  const blocks: any[] = [];
  let i = 0;
  while (i < lines.length) {
    const ln = lines[i];
    if (ln.startsWith('## ')) {
      blocks.push(<h4 key={i} className="mono" style={{
        margin: '18px 0 7px', fontSize: 11.5, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: 'var(--ember-2)', fontWeight: 600,
      }}>{ln.slice(3)}</h4>);
      i++;
    } else if (ln.startsWith('> ')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) { quote.push(lines[i].slice(2)); i++; }
      let attrib = null;
      if (i < lines.length && lines[i].startsWith('— ')) { attrib = lines[i].slice(2); i++; }
      blocks.push(
        <blockquote key={'q' + i} style={{
          margin: '12px 0', padding: '2px 0 2px 16px', borderLeft: '2px solid var(--plum)',
          fontFamily: 'var(--font-serif)', fontSize: 21, lineHeight: 1.34, color: 'var(--ink)',
        }}>
          {inlineRich(quote.join(' '), 'bq' + i)}
          {attrib && <div className="mono" style={{ marginTop: 8, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-3)', letterSpacing: '0.02em' }}>{attrib}</div>}
        </blockquote>
      );
    } else if (ln.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
      blocks.push(
        <ul key={'u' + i} style={{ margin: '8px 0', paddingLeft: 4, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
          {items.map((it, k) => (
            <li key={k} style={{ display: 'flex', gap: 10, alignItems: 'baseline', color: 'var(--ink-2)' }}>
              <span style={{ color: 'var(--ember)', flexShrink: 0, fontSize: 14, lineHeight: 1, marginTop: 2 }}>◆</span>
              <span>{inlineRich(it, 'li' + i + k)}</span>
            </li>
          ))}
        </ul>
      );
    } else if (ln.trim() === '') {
      i++;
    } else {
      const para: string[] = [];
      while (i < lines.length && lines[i].trim() !== '' && !/^(## |> |- )/.test(lines[i])) { para.push(lines[i]); i++; }
      blocks.push(<p key={'p' + i} style={{ margin: '9px 0', color: 'var(--ink-2)' }}>{inlineRich(para.join(' '), 'pp' + i)}</p>);
    }
  }
  return <div style={{ ...style }}>{blocks}</div>;
}

/* ---------- Temperatura do estudo (faísca → forjado) ---------- */
export const HEAT = [
  { key: 'faisca',  label: 'Faísca',  color: 'var(--ink-3)',   glow: 'rgba(158,148,133,0.0)' },
  { key: 'brasa',   label: 'Brasa',   color: '#C2703A',        glow: 'rgba(194,112,58,0.35)' },
  { key: 'chama',   label: 'Chama',   color: 'var(--ember)',   glow: 'rgba(232,122,59,0.5)' },
  { key: 'forjado', label: 'Forjado', color: 'var(--ember-2)', glow: 'rgba(242,154,96,0.65)' },
];
export function computeHeat(text: string) {
  const t = (text || '').trim();
  const chars = t.length;
  const headings = (t.match(/^##\s/gm) || []).length;
  const quotes = (t.match(/^>\s/gm) || []).length;
  const lists = (t.match(/^-\s/gm) || []).length;
  let s = Math.min(chars / 1100, 1) * 0.62 + Math.min(headings / 3, 1) * 0.24 + (quotes ? 0.09 : 0) + Math.min(lists / 4, 1) * 0.05;
  s = Math.min(s, 1);
  let level = 0;
  if (s >= 0.20) level = 1;
  if (s >= 0.46) level = 2;
  if (s >= 0.76) level = 3;
  return { score: s, level, ...HEAT[level] };
}
export function postHeat(post: any) {
  let txt = post.text || '';
  if (post.comment) txt += '\n' + post.comment;
  return computeHeat(txt);
}

/* ---------- Chips / metadados ---------- */
export function PageChip({ page, children }: any) {
  return (
    <span className="mono" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
      color: 'var(--ink-3)', background: 'var(--paper-3)', padding: '2px 8px',
      borderRadius: 999, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      <IcoPage size={11} sw={1.8} />{children || `p. ${page}`}
    </span>
  );
}
export function TypeBadge({ kind, size = 'sm' }: any) {
  const t = POST_TYPES[kind]; if (!t) return null;
  const Icon = t.Icon;
  const big = size === 'lg';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      color: t.accent, fontSize: big ? 12 : 11,
      fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 500,
    }}>
      <span style={{
        width: big ? 26 : 22, height: big ? 26 : 22, borderRadius: 7, display: 'grid', placeItems: 'center',
        background: t.soft, color: t.accent,
      }}><Icon size={big ? 15 : 13} /></span>
      {t.short}
    </span>
  );
}

/* ---------- Bottom sheet ---------- */
export function Sheet({ open, onClose, children, maxHeight = '92vh', label }: any) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: any) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div role="dialog" aria-label={label} style={{
      position: 'fixed', inset: 0, zIndex: 60, display: 'flex', flexDirection: 'column',
      justifyContent: 'flex-end',
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(8,6,5,0.62)', animation: 'e2FadeIn 0.2s ease both', backdropFilter: 'blur(2px)' }} />
      <div style={{
        position: 'relative', background: 'var(--paper-2)', borderTopLeftRadius: 22, borderTopRightRadius: 22,
        boxShadow: '0 -20px 60px rgba(0,0,0,0.25)', maxHeight, display: 'flex', flexDirection: 'column',
        animation: 'e2SheetUp 0.34s cubic-bezier(0.2,0.8,0.2,1) both', borderTop: '1px solid var(--line-strong)',
        maxWidth: 460, margin: '0 auto', width: '100%',
      }}>
        <div style={{ display: 'grid', placeItems: 'center', padding: '9px 0 3px', flexShrink: 0 }}>
          <div style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--line-strong)' }} />
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- Botão primário ember ---------- */
export function PrimaryBtn({ children, onClick, disabled, style, full }: any) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      background: disabled ? 'var(--paper-3)' : 'var(--ember)', color: disabled ? 'var(--ink-4)' : '#1a1208',
      fontWeight: 600, fontSize: 15, padding: '13px 20px', borderRadius: 12, width: full ? '100%' : 'auto',
      boxShadow: disabled ? 'none' : 'var(--shadow-ember)', transition: 'transform 0.1s, background 0.2s, opacity 0.2s',
      opacity: disabled ? 0.7 : 1, ...style,
    }}
    onPointerDown={(e: any) => !disabled && (e.currentTarget.style.transform = 'scale(0.97)')}
    onPointerUp={(e: any) => (e.currentTarget.style.transform = '')}
    onPointerLeave={(e: any) => (e.currentTarget.style.transform = '')}
    >{children}</button>
  );
}

/* ---------- helpers ---------- */
export const fmt = (n: number) => n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '') + 'k' : '' + n;
export const stripMd = (s: string) => (s || '').replace(/\*\*/g, '').replace(/(^|[^*])\*([^*]+)\*/g, '$1$2').replace(/^#{1,6}\s?/gm, '').replace(/^[>-]\s?/gm, '').replace(/^—\s?/gm, '').trim();

/* ---------- Lição (formato de ENSINO) ---------- */
export const LICAO_TAG = '§ensino§';
export function parseLicao(text: string) {
  if (!text || !text.startsWith(LICAO_TAG)) return { isLicao: false, concept: '', body: '' };
  const firstNl = text.indexOf('\n');
  const concept = (firstNl === -1 ? text : text.slice(0, firstNl)).replace(LICAO_TAG, '').trim();
  const body = firstNl === -1 ? '' : text.slice(firstNl + 1).replace(/^\n+/, '');
  return { isLicao: true, concept, body };
}
export const isLicao = (post: any) => post.kind === 'reflection' && (post.text || '').startsWith(LICAO_TAG);

/* ---------- CONFIG DE TIPOS (kind → identidade visual) ---------- */
export const POST_TYPES: any = {
  'reflection':    { label: 'Reflexão',   short: 'Reflexão',  Icon: IcoFlame,    accent: 'var(--ember)', soft: 'var(--ember-soft)' },
  'ensino':        { label: 'Ensinar',    short: 'Lição',     Icon: IcoTeach,    accent: 'var(--lapis)', soft: 'var(--lapis-soft)' },
  'citation':      { label: 'Citação',    short: 'Citação',   Icon: IcoQuote,    accent: 'var(--plum)',  soft: 'var(--plum-soft)' },
  'question':      { label: 'Pergunta',   short: 'Pergunta',  Icon: IcoQuestion, accent: 'var(--sage)',  soft: 'var(--sage-soft)' },
  'quiz':          { label: 'Quiz',       short: 'Quiz',      Icon: IcoQuiz,     accent: 'var(--lapis)', soft: 'var(--lapis-soft)' },
  'question-box':  { label: 'Caixa de perguntas', short: 'Caixa', Icon: IcoInbox, accent: 'var(--sage)', soft: 'var(--sage-soft)' },
  'leader-answer': { label: 'Resposta do líder', short: 'Líder', Icon: IcoCrown,  accent: 'var(--ember-2)', soft: 'var(--ember-soft)' },
  'insight-final': { label: 'Insight final', short: 'Insight', Icon: IcoTrophy,  accent: 'var(--ember)', soft: 'var(--ember-soft)' },
};

/* ---------- FILTROS ---------- */
export const STUDY_FILTERS = [
  { id: 'all', label: 'Tudo' },
  { id: 'finals', label: 'Insights' },
  { id: 'quiz', label: 'Quiz' },
  { id: 'questions', label: 'Perguntas' },
  { id: 'leader', label: 'Líder' },
  { id: 'saved', label: 'Salvos' },
];

/* ---------- REAÇÕES ---------- */
export const REACTIONS = [
  { id: 'tocou',  label: 'Tocou',   Icon: IcoTouch,    accent: 'var(--ember)' },
  { id: 'aprendi',label: 'Aprendi', Icon: IcoBulb,     accent: 'var(--lapis)' },
  { id: 'fogo',   label: 'Fogo',    Icon: IcoFlame,    accent: 'var(--ember-2)' },
  { id: 'salvar', label: 'Salvar',  Icon: IcoBookmark, accent: 'var(--sage)' },
];

/* ---------- LENTES (andaime do composer) ---------- */
export const LENSES: any = {
  reflection: [
    { id: 'livre', label: 'Livre', scaffold: '' },
    { id: 'estudo', label: 'Estudo', scaffold: '> \n— p. \n\n## O que entendi\n\n\n## O que ainda me incomoda\n\n\n## Como aplico\n' },
    { id: 'debate', label: 'Debate', scaffold: '## Minha posição\n\n\n## Por quê\n\n\n## E se eu estiver errado?\n' },
    { id: 'conexao', label: 'Conexão', scaffold: '## O que isso me lembrou\n\n\n## O que muda em mim\n' },
  ],
};
export const ENSINO_SCAFFOLD = '## Em palavras simples\n\n\n## Na prática\n\n\n## O erro mais comum\n';

export const readingTime = (txt: string) => Math.max(1, Math.round((txt || '').split(/\s+/).length / 180)) + ' min';
